// src/Interface/Public-Pages/PublicProjects.tsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './pStyles/PublicProjects.css'; // We will use a dedicated CSS file

// Type definitions for our data
interface Task {
  id: number;
  title: string;
  description: string;
  required_skills: string[];
}

interface Project {
  id: number;
  name: string;
  description: string;
  status: string;
  tasks?: Task[]; // Tasks can be optional
}

const PublicProjects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProjectId, setExpandedProjectId] = useState<number | null>(null);

  // Fetch all projects and their tasks from the backend
  useEffect(() => {
    const fetchProjectsAndTasks = async () => {
      setIsLoading(true);
      setError(null);
      try {
        // Step 1: Fetch all projects
        const projectsResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/projects`);
        if (!projectsResponse.ok) throw new Error('Failed to fetch projects');
        let projectsData: Project[] = await projectsResponse.json();

        // Step 2: For each project, fetch its tasks
        const projectsWithTasks = await Promise.all(
          projectsData.map(async (project) => {
            const tasksResponse = await fetch(`${process.env.REACT_APP_API_URL || 'https://mellow-commitment-production-bf3d.up.railway.app'}/api/projects/${project.id}/tasks`);
            if (!tasksResponse.ok) {
              console.error(`Failed to fetch tasks for project ${project.id}`);
              return { ...project, tasks: [] }; // Return project with empty tasks on failure
            }
            const tasksData: Task[] = await tasksResponse.json();
            return { ...project, tasks: tasksData };
          })
        );

        setProjects(projectsWithTasks);
      } catch (e) {
        console.error("API Error:", e);
        setError("Could not load projects at this time.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjectsAndTasks();
  }, []);

  // Function to toggle the expanded project
  const toggleProjectTasks = (projectId: number) => {
    setExpandedProjectId(prevId => (prevId === projectId ? null : projectId));
  };

  return (
    <div className="projects-page-new">
      <div className="container-new">
        {/* HERO SECTION */}
        <section className="projects-hero-new">
          <h1 className="projects-title-new">Find Your Calling</h1>
          <p className="projects-subtitle-new">
            Browse our current projects and discover where your skills can make the biggest impact.
          </p>
        </section>

        {/* PROJECTS GRID */}
        <section className="projects-grid-container">
          {isLoading && <p className="loading-text">Loading Projects...</p>}
          {error && <p className="error-text">{error}</p>}
          
          {!isLoading && !error && projects.map(project => (
            <div key={project.id} className={`project-card-new ${expandedProjectId === project.id ? 'expanded' : ''}`}>
              <div className="project-card-main-content">
                <div className="project-info">
                  <span className={`status-badge-new ${project.status.toLowerCase()}`}>{project.status}</span>
                  <h2 className="project-name-new">{project.name}</h2>
                  <p className="project-description-new">{project.description}</p>
                </div>
                <div className="project-actions">
                  <button onClick={() => toggleProjectTasks(project.id)} className="action-btn-new view-tasks">
                    {expandedProjectId === project.id ? 'Hide Tasks' : 'View Tasks'}
                  </button>
                  <Link to="/application" className="action-btn-new join-project">
                    Join Project
                  </Link>
                </div>
              </div>

              {/* EXPANDABLE TASKS SECTION */}
              <div className="project-tasks-accordion">
                <div className="tasks-content-wrapper">
                  <h3 className="tasks-title-new">Available Tasks</h3>
                  {project.tasks && project.tasks.length > 0 ? (
                    <div className="tasks-list-new">
                      {project.tasks.map(task => (
                        <div key={task.id} className="task-item-new">
                          <h4 className="task-name-new">{task.title}</h4>
                          <p className="task-description-new">{task.description}</p>
                          {task.required_skills && task.required_skills.length > 0 && (
                             <p className="task-skills-new"><strong>Skills:</strong> {task.required_skills.join(', ')}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="no-tasks-text">No specific tasks listed for this project yet.</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* JOIN COMMUNITY CTA */}
        <section className="join-cta-section">
            <h2 className="join-title-new">Ready to Make an Impact?</h2>
            <p className="join-subtitle-new">Become a part of our community and start your volunteer journey today.</p>
            <Link to="/application" className="cta-button primary">Create Your Account</Link>
        </section>
      </div>
    </div>
  );
};

export default PublicProjects;